import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '../../lib/api'
import { useAuthStore } from '../../stores/auth'
import { Button } from '../../components/ui/Button'
import { ChipuriFiltru } from '../../components/ui/ChipuriFiltru'
import { PanouLateral, SectiunePanou } from '../../components/ui/PanouLateral'
import { Pastila } from '../../components/ui/Pastila'
import { etichetaSellIn } from '@b4l/shared'
import { dataScurta, eroare, esteRestant, lei, type RandClient } from './comun'

/*
  Clienti fara comanda (reminderul cerut de Victor; prag 60 de zile, Adrian 27.10.2026; macheta
  aprobata de Madalin 27.10). Cine a trecut de pragul lui; pe fiecare: Am sunat, Amana, Nu mai
  urmari. Iese singur din lista la prima comanda noua. Regula e pe server (`faraComanda.service`).
*/

type Filtru = 'de_facut' | 'amanat' | 'nu_urmari' | 'pierdut'

const MOTIVE = [
  { cheie: 'sezonier', eticheta: 'Client sezonier' },
  { cheie: 'inchis', eticheta: 'Farmacia s-a închis' },
  { cheie: 'litigiu', eticheta: 'În litigiu' },
  { cheie: 'alt_motiv', eticheta: 'Alt motiv' },
] as const
const motivEticheta = (m: string | null) => MOTIVE.find((x) => x.cheie === m)?.eticheta ?? m ?? ''

const SURSA: Record<string, string> = {
  crm: 'comandă din CRM',
  erp: 'comandă din ERP',
  factura: 'factură',
}

/** De ce e clientul aici, spus pe limba agentului. */
export function deCe(c: RandClient): string {
  const p = c.pauza!
  return p.ritm != null && p.prag < 60
    ? `Comandă de obicei la ~${p.ritm} zile. Acum: ${p.zile}.`
    : `${p.zile} zile fără comandă (pragul e de 60).`
}

export function FaraComanda({ clienti }: { clienti: RandClient[] }) {
  const [filtru, setFiltru] = useState<Filtru>('de_facut')
  const [alesId, setAlesId] = useState<number | null>(null)
  const cuPauza = useMemo(
    () => clienti.filter((c) => c.pauza).sort((a, b) => b.pauza!.zile - a.pauza!.zile),
    [clienti],
  )
  const numar = (f: Filtru) => cuPauza.filter((c) => c.pauza!.stare === f).length
  // Cei sunati deja in pauza asta trec la coada listei „De făcut".
  const vizibili = cuPauza
    .filter((c) => c.pauza!.stare === filtru)
    .sort((a, b) => Number(!!a.pauza!.ultimaNota) - Number(!!b.pauza!.ultimaNota))
  const ales = cuPauza.find((c) => c.id === alesId) ?? null

  return (
    <div className="grid gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <ChipuriFiltru
          eticheta="Clienți fără comandă"
          ales={filtru}
          onAlege={setFiltru}
          chipuri={[
            { cheie: 'de_facut', eticheta: 'De făcut', numar: numar('de_facut') },
            { cheie: 'amanat', eticheta: 'Amânați', numar: numar('amanat') },
            { cheie: 'nu_urmari', eticheta: 'Nu mai urmăresc', numar: numar('nu_urmari') },
            // Peste un an fara comanda: nu se anunta si nu intra in „De făcut".
            { cheie: 'pierdut', eticheta: 'Peste 1 an', numar: numar('pierdut') },
          ]}
        />
        <p className="text-mic text-b4l-text-muted">
          Pragul: 60 de zile; mai devreme la cei care comandă des. Conturile naționale nu intră.
        </p>
      </div>

      {vizibili.length === 0 ? (
        <p className="rounded-xl border border-b4l-border bg-b4l-surface p-6 text-center text-corp text-b4l-text-muted">
          {filtru === 'de_facut'
            ? 'Toți clienții au comandat în ritmul lor. Nimic de sunat azi.'
            : filtru === 'amanat'
              ? 'Niciun client amânat.'
              : filtru === 'pierdut'
                ? 'Niciun client fără comandă de peste un an.'
                : 'Niciun client scos din urmărire.'}
        </p>
      ) : (
        <ul className="overflow-hidden rounded-xl border border-b4l-border bg-b4l-surface">
          {vizibili.map((c) => (
            <li key={c.id} className="border-b border-b4l-border last:border-b-0">
              <button
                type="button"
                onClick={() => setAlesId(c.id)}
                className="grid w-full grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-1.5 px-4 py-3 text-left hover:bg-b4l-bg-soft focus-visible:outline focus-visible:outline-2 focus-visible:outline-b4l-blue"
              >
                <span className="min-w-0">
                  <span className="block font-semibold text-b4l-text">{c.nume}</span>
                  <span className="block text-mic text-b4l-text-muted">{deCe(c)}</span>
                </span>
                <span className="text-right tabular-nums">
                  <span className="block text-base font-bold text-b4l-amber-dark">
                    {c.pauza!.zile} zile
                  </span>
                  <span className="block text-mic text-b4l-text-muted">
                    ultima: {dataScurta(c.pauza!.ultimaComanda)}
                  </span>
                </span>
                <span className="col-span-2 flex flex-wrap gap-1.5">
                  {c.pauza!.ritm != null && c.pauza!.prag < 60 && (
                    <Pastila ton="galben">peste ritmul lui</Pastila>
                  )}
                  {esteRestant(c) && <Pastila ton="rosu">sold restant {lei(c.restant)}</Pastila>}
                  {c.pauza!.ultimaNota && (
                    <Pastila ton="verde">sunat {dataScurta(c.pauza!.ultimaNota.data)}</Pastila>
                  )}
                  {c.pauza!.amanatPana && (
                    <Pastila ton="gri">amânat până pe {dataScurta(c.pauza!.amanatPana)}</Pastila>
                  )}
                  {c.pauza!.motiv && <Pastila ton="gri">{motivEticheta(c.pauza!.motiv)}</Pastila>}
                  {c.agent && <Pastila ton="gri">{c.agent}</Pastila>}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      <PanouLateral
        deschis={!!ales}
        onInchide={() => setAlesId(null)}
        titlu={ales?.nume}
        subtitlu={
          ales && (
            <>
              <Pastila ton="galben">{ales.pauza!.zile} zile fără comandă</Pastila>
              {ales.agent && <Pastila ton="gri">{ales.agent}</Pastila>}
            </>
          )
        }
      >
        {ales && <Panou c={ales} />}
      </PanouLateral>
    </div>
  )
}

function Panou({ c }: { c: RandClient }) {
  const qc = useQueryClient()
  const p = c.pauza!
  // Contabilitatea vede lista (orders:read), dar nu lucreaza pe ea.
  const poate = useAuthStore((s) => s.permissions).includes('clienti.acte:write')
  const [nota, setNota] = useState('')
  const [scrie, setScrie] = useState(false)
  const [motiv, setMotiv] = useState<(typeof MOTIVE)[number]['cheie'] | ''>('')
  const reimprospateaza = () => qc.invalidateQueries({ queryKey: ['clienti'] })

  const sunat = useMutation({
    mutationFn: async () =>
      api.post(`/clienti/${c.id}/interactiuni`, {
        fel: 'telefon',
        data: new Date().toISOString(),
        nota: nota.trim(),
      }),
    onSuccess: () => {
      toast.success('Notat. Apare și în fișa clientului.')
      setNota('')
      setScrie(false)
      void reimprospateaza()
    },
    onError: (e) => toast.error(eroare(e)),
  })
  const schimba = useMutation({
    mutationFn: async (body: Record<string, unknown>) =>
      api.post(`/clienti/${c.id}/fara-comanda`, body),
    onSuccess: (_r, body) => {
      toast.success(
        body.actiune === 'amana'
          ? `Amânat ${String(body.zile)} zile.`
          : body.actiune === 'nu_urmari'
            ? 'Scos din urmărire până la următoarea comandă.'
            : 'Înapoi la „De făcut”.',
      )
      setMotiv('')
      void reimprospateaza()
    },
    onError: (e) => toast.error(eroare(e)),
  })

  return (
    <>
      <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-1.5 text-corp">
        <dt className="text-b4l-text-muted">Ultima comandă</dt>
        <dd className="tabular-nums">
          {dataScurta(p.ultimaComanda)} · {SURSA[p.sursa ?? ''] ?? '—'}
        </dd>
        <dt className="text-b4l-text-muted">Ritm obișnuit</dt>
        <dd>
          {p.ritm != null ? `~${p.ritm} zile între comenzi` : 'prea puține comenzi în 12 luni'}
        </dd>
        <dt className="text-b4l-text-muted">De ce e aici</dt>
        <dd>{deCe(c)}</dd>
        <dt className="text-b4l-text-muted">Sold</dt>
        <dd className={esteRestant(c) ? 'font-semibold text-b4l-red-dark' : ''}>
          {esteRestant(c)
            ? `${lei(c.restant)} restant${c.zileIntarziere ? `, ${c.zileIntarziere} zile peste termen` : ''}`
            : 'nimic restant'}
        </dd>
        <dt className="text-b4l-text-muted">{etichetaSellIn()}</dt>
        <dd className="tabular-nums">{lei(c.sellIn)}</dd>
      </dl>

      {!poate ? null : p.stare === 'de_facut' || p.stare === 'pierdut' ? (
        <SectiunePanou titlu="Ce faci">
          <div className="grid gap-3">
            {scrie ? (
              <div className="grid gap-2">
                <label htmlFor="nota-sunat" className="text-mic text-b4l-text-muted">
                  Ce s-a vorbit (rămâne în fișa clientului)
                </label>
                <textarea
                  id="nota-sunat"
                  value={nota}
                  onChange={(e) => setNota(e.target.value)}
                  rows={3}
                  maxLength={2000}
                  placeholder="ex.: comandă după 1 noiembrie, așteaptă inventarul"
                  className="rounded-caseta border border-b4l-border bg-b4l-surface px-2.5 py-2 text-corp text-b4l-text"
                />
                <div className="flex gap-2">
                  <Button
                    onClick={() => sunat.mutate()}
                    loading={sunat.isPending}
                    disabled={nota.trim().length < 2}
                  >
                    Salvează
                  </Button>
                  <Button variant="secondary" onClick={() => setScrie(false)}>
                    Renunță
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                <Button onClick={() => setScrie(true)}>Am sunat</Button>
                {([7, 14, 30] as const).map((z) => (
                  <Button
                    key={z}
                    variant="secondary"
                    onClick={() => schimba.mutate({ actiune: 'amana', zile: z })}
                    disabled={schimba.isPending}
                  >
                    Amână {z} zile
                  </Button>
                ))}
              </div>
            )}
            <div className="flex flex-wrap items-center gap-2">
              <label htmlFor="motiv-nu-urmari" className="text-mic text-b4l-text-muted">
                Nu mai urmări până comandă din nou:
              </label>
              <select
                id="motiv-nu-urmari"
                value={motiv}
                onChange={(e) => setMotiv(e.target.value as typeof motiv)}
                className="rounded-caseta border border-b4l-border bg-b4l-surface px-2 py-1.5 text-corp text-b4l-text"
              >
                <option value="">alege motivul</option>
                {MOTIVE.map((m) => (
                  <option key={m.cheie} value={m.cheie}>
                    {m.eticheta}
                  </option>
                ))}
              </select>
              <Button
                variant="secondary"
                disabled={!motiv || schimba.isPending}
                onClick={() => schimba.mutate({ actiune: 'nu_urmari', motiv })}
              >
                Nu mai urmări
              </Button>
            </div>
          </div>
        </SectiunePanou>
      ) : (
        <SectiunePanou titlu="Stare">
          <div className="grid gap-2 text-corp">
            <p>
              {p.stare === 'amanat'
                ? p.zile > 365
                  ? `Amânat până pe ${dataScurta(p.amanatPana)} inclusiv. A doua zi revine în „Peste 1 an”, fără notificare.`
                  : `Amânat până pe ${dataScurta(p.amanatPana)} inclusiv. A doua zi revine în „De făcut” și primești din nou notificare.`
                : `Nu mai e urmărit: ${motivEticheta(p.motiv)}. Revine singur dacă comandă și apoi iar se oprește.`}
            </p>
            <div>
              <Button
                variant="secondary"
                onClick={() => schimba.mutate({ actiune: 'reia' })}
                loading={schimba.isPending}
              >
                Înapoi la „De făcut”
              </Button>
            </div>
          </div>
        </SectiunePanou>
      )}

      <SectiunePanou titlu="Ultima notă">
        {p.ultimaNota ? (
          <p className="text-corp">
            „{p.ultimaNota.nota}”
            <span className="block text-mic text-b4l-text-muted">
              {p.ultimaNota.de} · {dataScurta(p.ultimaNota.data)}
            </span>
          </p>
        ) : (
          <p className="text-corp text-b4l-text-muted">Nimic notat de la ultima comandă.</p>
        )}
      </SectiunePanou>

      <Link to={`/clienti/${c.id}`} className="text-corp font-medium text-b4l-blue hover:underline">
        Deschide fișa clientului
      </Link>
    </>
  )
}
